import React, { KeyboardEvent, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { IconMail } from '@tabler/icons-react';
import { notifications } from '@mantine/notifications';
import { ReactComponent as IconError } from "src/assets/icons/toast-error.svg";
import { ReactComponent as IconSuccess } from "src/assets/icons/toast-success.svg";
import useCleverTapEvents from 'src/events/useGetEvents';
import { EMAIL_EVENT } from 'src/events/constant';
import { ScreenWrapper } from '../../components';
import { OnboardingProgress } from '../../components/onboardingProgress';
import { addEmail, clearEmailFormError, setEmailFormStatus } from './emailFormSlice';
import { useStyles } from './addEmail.styles';
import { Button, TextInput } from '../../components/atoms';

export const AddEmail = (): JSX.Element => {
  const { classes } = useStyles();
  const dispatch = useDispatch();
  const emailFormState = useSelector((state: any) => state.emailData);
  const { email: emailId } = useSelector((state: any) => state.emailData);

  const [email, setEmail] = useState<string>(emailId || '');
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const navigate = useNavigate();
  const isValidEmail = (mail: string) => mail.match(/^\S+@\S+\.\S+$/);
  const isButtonDisabled = !isValidEmail(email);
  const { TriggerEvent } = useCleverTapEvents();

  useEffect(() => {
    if (emailFormState.status === 'success') {
      dispatch(setEmailFormStatus('idle'));
      notifications.show({
        color: 'green',
        message: 'Successfully sent OTP!',
        icon: <IconSuccess />,
      });
      navigate('/verify-email');
    } else if (emailFormState.status === 'error') {
      dispatch(clearEmailFormError());
      notifications.show({
        color: 'red',
        message: emailFormState.error.message,
        icon: <IconError />,
      });
    }
  }, [emailFormState.status]);

  const handleEmailSubmit = async () => {
    if (!isButtonDisabled || !isLoading) {
      setIsLoading(true);
      if (!isValidEmail(email)) {
        setIsLoading(false);
        setErrorMessage('Enter valid Email Id');
      } else {
        await dispatch(addEmail({
          email,
        }));
        TriggerEvent(EMAIL_EVENT.emailSubmit, { emailId: email, errorMessage: errorMessage || "" });
      }
    }
  };

  const onNextClick = () => {
    handleEmailSubmit();
  };

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEmail(e.target.value);
    setErrorMessage('');
  };

  const handleKeyPress = (event: KeyboardEvent) => {
    if (event.key === 'Enter') {
      handleEmailSubmit();
    }
  };

  return (
    <ScreenWrapper>
      <div className="csMainContainer">
        <div className="csHeader">
          <div className="csProgressWrapper">
            <OnboardingProgress progress={4} />
          </div>
        </div>
        <div className={classes.contentContainer}>
          <h2 className="csHeading">
            Email address
          </h2>
          <p className="csDescription">
            All communications related to your trading account will be sent to this email address
          </p>
          <TextInput
            type="email"
            name="email"
            id="email"
            title="Enter your Email address"
            placeholder="Enter your email address"
            autoComplete="off"
            // eslint-disable-next-line jsx-a11y/no-autofocus
            autoFocus
            required
            onChange={handleEmailChange}
            onKeyDown={handleKeyPress}
            value={email}
            icon={<IconMail stroke="1.2px" />}
            error={errorMessage}
          />
          <p className={`csDescription ${classes.inputDescription}`}>
            Note: Please check your SPAM folder in case you didn&apos;t receive the OTP in Inbox
          </p>
        </div>
        <div className="csFooter">
          <Button
            disabled={isButtonDisabled}
            onClick={onNextClick}
            loading={emailFormState.status === 'loading'}
          >
            Next
          </Button>
        </div>
      </div>
    </ScreenWrapper>
  );
};

export default AddEmail;
